import React, { useEffect, useRef, useState, useMemo } from 'react';

interface ProductPreviewProps {
    widthMm: number;
    heightMm: number;
    imageUrl?: string;
    frameType?: string;
}

export const ProductPreview: React.FC<ProductPreviewProps> = ({
    widthMm,
    heightMm,
    imageUrl,
    frameType
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [images, setImages] = useState<Record<string, HTMLImageElement>>({});
    const [loaded, setLoaded] = useState(false);
    const [artworkImage, setArtworkImage] = useState<HTMLImageElement | null>(null);
    const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

    // Constants
    const FRAME_WIDTH_PX = 54;
    const DPI = 72;
    const PIXELS_PER_MM = DPI / 25.4;

    const hasFrame = !!frameType && frameType !== 'none';
    const FRAME_OVERLAP_MM = hasFrame ? 9 : 0;
    const TOTAL_OVERLAP_MM = FRAME_OVERLAP_MM * 2;

    // Load artwork image
    useEffect(() => {
        if (!imageUrl) {
            setArtworkImage(null);
            return;
        }
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => setArtworkImage(img);
        img.src = imageUrl;
    }, [imageUrl]);

    // Load simulator assets
    useEffect(() => {
        const loadImages = async () => {
            const imagePaths = {
                cornerTopLeft: '/assets/simulator/aluminum_black_corner_top_left.png',
                cornerTopRight: '/assets/simulator/aluminum_black_corner_top_right.png',
                edgeTop: '/assets/simulator/aluminum_black_edge_top.png',
                edgeBottom: '/assets/simulator/aluminum_black_edge_bottom.png',
                edgeLeft: '/assets/simulator/aluminum_black_edge_left.png',
                edgeRight: '/assets/simulator/aluminum_black_edge_right.png',
            };

            const loadedImages: Record<string, HTMLImageElement> = {};
            const promises = Object.entries(imagePaths).map(([key, src]) => {
                return new Promise<void>((resolve) => {
                    const img = new Image();
                    img.onload = () => {
                        loadedImages[key] = img;
                        resolve();
                    };
                    img.onerror = () => {
                        console.warn(`Failed to load image: ${src}`);
                        resolve();
                    };
                    img.src = src;
                });
            });

            await Promise.all(promises);
            setImages(loadedImages);
            setLoaded(true);
        };

        loadImages();
    }, []);

    // Monitor container size
    useEffect(() => {
        if (!containerRef.current) return;
        const updateSize = () => {
            if (containerRef.current) {
                setContainerSize({
                    width: containerRef.current.clientWidth,
                    height: containerRef.current.clientHeight
                });
            }
        };
        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    // Layout Calculation for Product Mode
    const layout = useMemo(() => {
        if (containerSize.width === 0 || containerSize.height === 0) return null;

        const paperWidthMm = widthMm;
        const paperHeightMm = heightMm;

        const frameWidthMm = hasFrame ? (FRAME_WIDTH_PX / PIXELS_PER_MM) : 0;
        const innerWidthMm = paperWidthMm - TOTAL_OVERLAP_MM;
        const innerHeightMm = paperHeightMm - TOTAL_OVERLAP_MM;

        const outerWidthMm = innerWidthMm + (frameWidthMm * 2);
        const outerHeightMm = innerHeightMm + (frameWidthMm * 2);

        const padding = 40;
        const availWidth = containerSize.width - padding * 2;
        const availHeight = containerSize.height - padding * 2;

        const outerWidthPxRaw = outerWidthMm * PIXELS_PER_MM;
        const outerHeightPxRaw = outerHeightMm * PIXELS_PER_MM;

        const scaleX = availWidth / outerWidthPxRaw;
        const scaleY = availHeight / outerHeightPxRaw;
        const scale = Math.min(scaleX, scaleY);

        const canvasTop = (containerSize.height - (outerHeightPxRaw * scale)) / 2;
        const canvasLeft = (containerSize.width - (outerWidthPxRaw * scale)) / 2;

        return {
            scale,
            canvasTop,
            canvasLeft,
            outerWidthMm,
            outerHeightMm,
            innerWidthMm,
            innerHeightMm
        };
    }, [widthMm, heightMm, containerSize, hasFrame, FRAME_OVERLAP_MM, TOTAL_OVERLAP_MM]);

    // Canvas Rendering
    useEffect(() => {
        if (!loaded || !canvasRef.current || !layout) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const { scale, outerWidthMm, outerHeightMm } = layout;

        const dpr = window.devicePixelRatio || 1;
        const finalWidth = outerWidthMm * PIXELS_PER_MM * scale;
        const finalHeight = outerHeightMm * PIXELS_PER_MM * scale;

        canvas.width = finalWidth * dpr;
        canvas.height = finalHeight * dpr;
        canvas.style.width = `${finalWidth}px`;
        canvas.style.height = `${finalHeight}px`;
        canvas.style.position = 'absolute';
        canvas.style.top = `${layout.canvasTop}px`;
        canvas.style.left = `${layout.canvasLeft}px`;

        ctx.scale(dpr, dpr);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.clearRect(0, 0, finalWidth, finalHeight);

        // Draw Artwork
        if (artworkImage) {
            const frameWidthPxScaled = hasFrame ? (FRAME_WIDTH_PX * scale) : 0;
            const overlapPxScaled = hasFrame ? (FRAME_OVERLAP_MM * PIXELS_PER_MM * scale) : 0;

            const paperX = frameWidthPxScaled - overlapPxScaled;
            const paperY = frameWidthPxScaled - overlapPxScaled;
            const paperW = widthMm * PIXELS_PER_MM * scale;
            const paperH = heightMm * PIXELS_PER_MM * scale;

            ctx.fillStyle = '#ffffff';
            ctx.fillRect(paperX, paperY, paperW, paperH);

            const imgAspect = artworkImage.width / artworkImage.height;
            const paperAspect = paperW / paperH;

            let drawW, drawH, drawX, drawY;

            if (imgAspect > paperAspect) {
                drawW = paperW;
                drawH = paperW / imgAspect;
                drawX = paperX;
                drawY = paperY + (paperH - drawH) / 2;
            } else {
                drawH = paperH;
                drawW = paperH * imgAspect;
                drawY = paperY;
                drawX = paperX + (paperW - drawW) / 2;
            }

            ctx.save();
            ctx.beginPath();
            ctx.rect(paperX, paperY, paperW, paperH);
            ctx.clip();
            ctx.drawImage(artworkImage, drawX, drawY, drawW, drawH);
            ctx.restore();
        }

        // Draw Frame
        if (hasFrame) {
            const scaledCornerSize = Math.max(images.cornerTopLeft?.width || 0, images.cornerTopLeft?.height || 0) * scale;
            const scaledFrameWidth = FRAME_WIDTH_PX * scale;

            const drawEdge = (side: 'top' | 'bottom' | 'left' | 'right', x: number, y: number, w: number, h: number) => {
                let img;
                switch (side) {
                    case 'top': img = images.edgeTop; break;
                    case 'bottom': img = images.edgeBottom; break;
                    case 'left': img = images.edgeLeft; break;
                    case 'right': img = images.edgeRight; break;
                }
                if (!img) return;

                if (side === 'top' || side === 'bottom') {
                    ctx.drawImage(img, 0, 0, img.width, img.height, x, y, w, scaledFrameWidth);
                } else {
                    ctx.drawImage(img, 0, 0, img.width, img.height, x, y, scaledFrameWidth, h);
                }
            };

            const drawCorner = (pos: 'tl' | 'tr' | 'bl' | 'br', x: number, y: number) => {
                let img;
                let rotation = 0;
                switch (pos) {
                    case 'tl': img = images.cornerTopLeft; break;
                    case 'tr': img = images.cornerTopRight; break;
                    case 'bl': img = images.cornerTopRight; rotation = 180; break;
                    case 'br': img = images.cornerTopLeft; rotation = 180; break;
                }
                if (!img) return;

                const size = scaledCornerSize;
                ctx.save();
                const cx = x + size / 2;
                const cy = y + size / 2;
                ctx.translate(cx, cy);
                ctx.rotate(rotation * Math.PI / 180);
                ctx.translate(-cx, -cy);
                ctx.drawImage(img, x, y, size, size);
                ctx.restore();
            };

            const topEdgeWidth = finalWidth - scaledCornerSize * 2;
            if (topEdgeWidth > 0) drawEdge('top', scaledCornerSize, 0, topEdgeWidth, scaledFrameWidth);

            const bottomEdgeWidth = finalWidth - scaledCornerSize * 2;
            if (bottomEdgeWidth > 0) drawEdge('bottom', scaledCornerSize, finalHeight - scaledFrameWidth, bottomEdgeWidth, scaledFrameWidth);

            const leftEdgeHeight = finalHeight - scaledCornerSize * 2;
            if (leftEdgeHeight > 0) drawEdge('left', 0, scaledCornerSize, scaledFrameWidth, leftEdgeHeight);

            const rightEdgeHeight = finalHeight - scaledCornerSize * 2;
            if (rightEdgeHeight > 0) drawEdge('right', finalWidth - scaledFrameWidth, scaledCornerSize, scaledFrameWidth, rightEdgeHeight);

            drawCorner('tl', 0, 0);
            drawCorner('tr', finalWidth - scaledCornerSize, 0);
            drawCorner('br', finalWidth - scaledCornerSize, finalHeight - scaledCornerSize);
            drawCorner('bl', 0, finalHeight - scaledCornerSize);
        }

    }, [loaded, layout, artworkImage, hasFrame, images, widthMm, heightMm, FRAME_OVERLAP_MM, PIXELS_PER_MM, FRAME_WIDTH_PX]);

    return (
        <div
            ref={containerRef}
            className="relative w-full h-full overflow-hidden flex items-center justify-center bg-gray-50"
        >
            <canvas ref={canvasRef} />
        </div>
    );
};
